import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { auth, db } from '../firebase';

const CartContext = createContext();
const CART_STORAGE_KEY = 'fairway_hunters_cart';

const normalizeCartItem = (item) => {
  if (!item || typeof item !== 'object') return null;

  const itemId = typeof item.itemId === 'string' ? item.itemId.trim() : '';
  const name = typeof item.name === 'string' ? item.name.trim() : '';
  const price = Number(item.price);
  const quantity = Number(item.quantity);
  const images = Array.isArray(item.images)
    ? item.images.filter((img) => typeof img === 'string')
    : [];

  if (!itemId || !name || !Number.isFinite(price) || price < 0) return null;

  const safeQuantity = Number.isFinite(quantity)
    ? Math.max(1, Math.floor(quantity))
    : 1;

  return {
    itemId,
    name,
    price: Math.round(price),
    quantity: safeQuantity,
    images,
  };
};

const sanitizeCart = (maybeCart) => {
  if (!Array.isArray(maybeCart)) return [];
  return maybeCart
    .map(normalizeCartItem)
    .filter(Boolean);
};

const readCartFromStorage = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return sanitizeCart(parsed);
  } catch {
    return [];
  }
};

const writeCartToStorage = (cart) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch {
    // Ignore write failures (e.g. private mode/storage limits)
  }
};

const mergeCartItems = (base = [], incoming = []) => {
  const mergedById = new Map();

  [...sanitizeCart(base), ...sanitizeCart(incoming)].forEach((item) => {
    const existing = mergedById.get(item.itemId);
    if (!existing) {
      mergedById.set(item.itemId, { ...item });
      return;
    }

    mergedById.set(item.itemId, {
      ...existing,
      ...item,
      quantity: (existing.quantity || 0) + (item.quantity || 0),
    });
  });

  return Array.from(mergedById.values());
};

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readCartFromStorage());
  const [userId, setUserId] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);

  // Ensure anonymous auth on mount, then load cart from Firestore
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);

        try {
          const cartDoc = await getDoc(doc(db, 'carts', user.uid));
          const firestoreCart = cartDoc.exists() ? cartDoc.data().items || [] : [];
          const localCart = readCartFromStorage();

          // Merge local + remote so refreshes never lose pre-auth cart actions.
          const mergedCart = mergeCartItems(firestoreCart, localCart);
          setCart(mergedCart);
          writeCartToStorage(mergedCart);
          await setDoc(doc(db, 'carts', user.uid), { items: mergedCart });
        } catch (e) {
          console.error('Failed to load cart from Firestore:', e);
        }
      } else {
        // Sign in anonymously so we have a stable user ID for the cart
        try {
          await signInAnonymously(auth);
        } catch (e) {
          console.error('Anonymous sign-in failed:', e);
        }
      }
      setCartLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Persist cart locally on every change so refresh always restores immediately.
  useEffect(() => {
    writeCartToStorage(cart);
  }, [cart]);

  // Sync local cart state to Firestore once we have a user.
  useEffect(() => {
    if (!userId) return;

    const syncCart = async () => {
      try {
        await setDoc(doc(db, 'carts', userId), { items: cart });
      } catch (e) {
        console.error('Failed to sync cart:', e);
      }
    };

    syncCart();
  }, [cart, userId]);

  const addToCart = (item) => {
    // item: { itemId, name, price (cents), images, quantity }
    const normalizedItem = normalizeCartItem(item);
    if (!normalizedItem) return;

    setCart((prev) => {
      const existing = prev.find((i) => i.itemId === normalizedItem.itemId);
      return existing
        ? prev.map((i) =>
            i.itemId === normalizedItem.itemId
              ? { ...i, quantity: i.quantity + normalizedItem.quantity }
              : i
          )
        : [...prev, normalizedItem];
    });
  };

  const removeFromCart = (itemId) => {
    setCart((prev) => prev.filter((i) => i.itemId !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) return;
    setCart((prev) => prev.map((i) => (i.itemId === itemId ? { ...i, quantity } : i)));
  };

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        userId,
        cartLoading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
